<template>
  <div class="webrtc-view">
    <div class="webrtc-view__tracks">
      <div class="webrtc-view__not-connected" v-if="!isPatientCameraPlaying">
        <p class="webrtc-view__connecting-text" v-if="connecting">Connecting ...</p>
        <button v-if="cancelable || !connecting" @click="cancelable ? stopVideo() : startVideo()"
          :class="{ 'is-danger': cancelable }" class="button is-primary webrtc-view__connect-btn">{{ connectButtonText
          }}</button>
      </div>
      <div class="webrtc-view__video-container">
        <div class="open-mic-warning" v-show="!muted && isPatientCameraPlaying">
          MIC IS OPEN
        </div>
        <teleport to=".quality-indicator-teleport" defer>
          <quality-indicator v-if="isPatientCameraPlaying && isQualityIndicatorEnabled" ref="qualityIndicator"
            is-video-meet :wifi="wifiMetrics" :is-wifi-network-interface="isWifiNetworkInterface" />
        </teleport>
        <!-- DEMO: remove this block when done -->
        <div v-if="overlayConfig && isPatientCameraPlaying" class="webrtc-overlay-bar"
          :style="{ height: overlayConfig.height + 'px', width: (overlayConfig.width || 100) + '%' }">
          <iframe :src="overlayIframeSrc" class="webrtc-overlay-iframe"></iframe>
        </div>
        <video id="webrtc-view__patient-view" autoplay playsinline v-show="isPatientCameraPlaying" ref="webrtcView"
          data-private></video>
        <!-- hidden, unmuted sink so screen-share audio stays audible regardless of which video tile shows the screen-share picture -->
        <audio id="webrtc-view__screenshare-audio" autoplay data-private></audio>
        <blinds :model-value="effectiveBlinds" @blinds-toggled="onLocalBlindsToggle" :button-enabled="!isGuestUser"
          v-show="isPatientCameraPlaying && blindsClosed"></blinds>
        <div class="overlay-container">
          <div id="webrtc-view__other-participants" v-show="otherParticipants.length > 0 || isPatientCameraPlaying">
            <!-- local participant -->
            <div v-if="isPatientCameraPlaying && videoSettings?.activateVideo" v-show="!videoSettings.isPreview"
              class="participant-container" @click="swapWithMain($event)">
              <video id="webrtc-view__local-view" autoplay playsinline muted="true" data-private></video>
              <div class="participant-name">{{ labelFor('webrtc-view__local-view', 'You') }}</div>
            </div>
            <!-- local participant screen share -->
            <div v-if="isPatientCameraPlaying && videoSettings?.activateVideo && localScreenShareActive"
              v-show="!videoSettings.isPreview" class="participant-container" @click="swapWithMain($event)">
              <video id="webrtc-view__local-screenshare-view" autoplay playsinline muted="true" data-private></video>
              <div class="participant-name">{{ labelFor('webrtc-view__local-screenshare-view', 'Your Screen') }}</div>
            </div>
            <!-- other participant screen share -->
            <div v-if="isPatientCameraPlaying && otherScreenShareActive" v-show="!videoSettings.isPreview"
              class="participant-container" @click="swapWithMain($event)">
              <video id="webrtc-view__other-screenshare-view" autoplay playsinline muted="true" data-private></video>
              <div class="participant-name">{{ labelFor('webrtc-view__other-screenshare-view', otherScreenShareLabel) }}
              </div>
            </div>
            <!-- other participants -->
            <div v-for="p in otherParticipants" :key="p.participantId" class="participant-container"
              @click="swapWithMain($event)">
              <div class="participant-avatar" v-if="!participantsVideoActive[p.participantId]"
                :style="{ backgroundColor: getAvatarColor(p.participantId) }">
                {{ getAvatarInitial(p.displayName, p.participantId) }}
              </div>
              <!-- NB: other elements on the page may use this participant id so make sure this is unique -->
              <video autoplay playsinline :id="`other-video-${p.participantId}`" data-private></video>
              <div class="participant-name">{{ labelFor(`other-video-${p.participantId}`, p.displayName ||
                p.participantId) }}</div>
            </div>
          </div>
          <div class="bottom-row">
            <ptz-presets v-show="isPatientCameraPlaying && isExpanded && meetingMetaData.is_ptz"
                         :element="$refs.webrtcView" :webrtcSession="webrtcSession"
                         :video-device-id="meetingMetaData?.device?.id" :skip-loading="true"></ptz-presets>
            <digital-ptz v-show="isPatientCameraPlaying && isExpanded && !meetingMetaData.is_ptz"
                         :popup-mode="isExpanded" class="video-ptz" :element="$refs.webrtcView" :webrtcSession="webrtcSession"
                         :supports-night-mode="supportsNightMode"></digital-ptz>
            <ptz v-show="isPatientCameraPlaying && isExpanded && meetingMetaData.is_ptz"
                 :supports-night-mode="supportsNightMode" :webrtcSession="webrtcSession" :popup-mode="isExpanded"
                 class="video-ptz" :element="$refs.webrtcView">
            </ptz>
          </div>
          <!-- disable transcription of patient
            <live-transcription :active="transcriptionEnabled && isPatientCameraPlaying"
              :audio-input-stream="getPatientAudioStream()" :patient-monitor-id="cachedPatientMonitorId"
              ref="liveTranscription">
            </live-transcription>
            -->
        </div>
        <div class="webrtc-view__video-controls" :class="{ 'webrtc-view__video-controls--expanded': isExpanded }"
          v-show="isPatientCameraPlaying">
          <button @click="stopVideo" class="button hangup-button webrtc-view__video-control">
            <i class="mdi mdi-phone-hangup" aria-hidden="true"></i>
          </button>
          <button v-if="!videoSettings.isPreview" @click="toggleMute" class="button webrtc-view__video-control">
            <i :class="['mdi', muted ? 'mdi-microphone-off' : 'mdi-microphone']" aria-hidden="true"></i>
          </button>
          <button v-if="!videoSettings.isPreview && isClosedCaptionsEnabled" @click="toggleTranscription"
            class="button webrtc-view__video-control">
            <img :src="transcriptionEnabled ? ccOnIcon : ccSlashIcon" alt="Closed Captions" class="cc-icon">
          </button>
          <button v-if="!videoSettings.isPreview && isScreenShareEnabled" @click="toggleScreenShare"
            :disabled="otherScreenShareActive && !localScreenShareActive"
            :class="['button webrtc-view__video-control', { 'is-success': localScreenShareActive }]">
            <i :class="['mdi', localScreenShareActive ? 'mdi-monitor-off' : 'mdi-monitor-share']"
              aria-hidden="true"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import to from "@/lib/to";
import { mapState, mapMutations } from "vuex";
import { markRaw } from "vue";
import DigitalPtz from "@/components/DigitalPtz";
import Ptz from "@/components/Ptz";
import PtzPresets from "@/components/PtzPresets";
import { WebRTCSession, SessionTypes, SessionEvents } from "@/lib/webrtc";
import { buildViewerTelemetryOptions, sendIncompleteSetupRecord } from "@/lib/webrtc/viewerTelemetryOptions";
import { describeWebrtcError } from "@/lib/webrtc/mediaErrors";
import { isVideoH265Capable } from "@/lib/webrtc/capabilities"
import { v4 as uuidv4 } from 'uuid'
import { LOG_TYPES } from "@/lib/Log";
import { getLogger } from "@/lib/logger";
import QualityIndicator from "@/components/QualityIndicator.vue";
import Blinds from "@/components/Blinds";
import { TranscriptionSession } from "@/lib/webrtc";
import * as semver from 'semver';
import ccOnIcon from "@/assets/icons/cc-on.png";
import ccSlashIcon from "@/assets/icons/cc-slash.png";
import { resolveOverlayConfig, buildOverlayIframeSrc } from "@/lib/OverlayDemo"; // DEMO: remove when done

const logger = getLogger("video");

export default {
  name: "WebrtcView",
  components: { DigitalPtz, Ptz, PtzPresets, QualityIndicator, Blinds },
  props: {
    startAutomatically: {
      type: Boolean,
      required: false,
      default: false
    },
    isExpanded: {
      type: Boolean,
      required: false,
      default: false
    },
    videoSettings: {
      type: Object,
      required: false,
      default: () => {
        return {
          doorKnock: true,
          visitorName: '',
          activateVideo: false,
          isPreview: false,
        }
      }
    },
    meetingMetaData: {
      type: Object,
      required: false,
      default: () => { return {} }
    },
    blindsClosed: {
      type: Boolean,
      default: false
    },
    // When true, failures route to the console instead of a $buefy modal. Used by embedded/hidden
    // instances (e.g. the central-monitoring kiosk two-way-video meeting) that must stay invisible
    // to the observer; teardown still flows through the @close event.
    suppressDialogs: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    // DEMO: remove this method when done
    loadOverlay() {
      this.overlayConfig = resolveOverlayConfig(this.meetingMetaData?.patient?.id)
    },
    getAvatarColor(id) {
      const colors = ['#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c', '#e67e22', '#16a085'];
      if (!id) return colors[0];
      let hash = 0;
      for (let i = 0; i < id.length; i++) {
        hash = id.charCodeAt(i) + ((hash << 5) - hash);
      }
      return colors[Math.abs(hash) % colors.length];
    },
    getAvatarInitial(displayName, participantId) {
      const name = displayName || participantId || '';
      return name.charAt(0).toUpperCase() || '?';
    },
    hasLiveVideoTrack(mediaStream) {
      if (!mediaStream) return false;
      try {
        return mediaStream.getVideoTracks().some(t => t.readyState === 'live');
      } catch (e) {
        logger.warn('failed to check video tracks', e);
        return false;
      }
    },
    async connectParticipantMedia(participant) {
      // NOTE: this may be called multiple times for the same participant
      const isPatient = participant.isPatient;
      logger.debug('participant media connected', { peer_id: participant.participantId, is_patient: isPatient });
      if (!isPatient) {
        const pIndex = this.otherParticipants.findIndex((p) => p.participantId === participant.participantId);
        if (pIndex === -1) {
          // need to add this to array first so that vue will allocate the div element
          this.otherParticipants.push(participant);
          logger.debug('participant added to list', { peer_id: participant.participantId, total: this.otherParticipants.length });
        } else {
          logger.debug('participant already in list', { peer_id: participant.participantId });
          // still need to continue processing in case the stream changed
        }
        if (participant.participantId) {
          this.participantsVideoActive[participant.participantId] = this.hasLiveVideoTrack(participant.mediaStream);
          try {
            participant.mediaStream?.getVideoTracks().forEach(track => {
              track.addEventListener('ended', () => {
                this.participantsVideoActive[participant.participantId] = false;
              });
            });
          } catch (e) {
            logger.warn('failed to attach track ended listeners', { peer_id: participant.participantId }, e);
          }
        }
      }

      const container = isPatient ? document.getElementById('webrtc-view__patient-view') : document.getElementById('webrtc-view__other-participants');
      if (isPatient) {
        if (container.srcObject !== participant.mediaStream) {
          logger.info('patient stream attached', { stream_id: participant.mediaStream.id });
          container.srcObject = participant.mediaStream;
        }
        // Preload patient monitor ID for future use (Aimee, transcription, etc.)
        await this.getPatientMonitorId()
      } else {
        this.$nextTick(() => {
          // make sure the video element is in the DOM before setting the stream
          const pDiv = document.getElementById(`other-video-${participant.participantId}`);
          if (!pDiv) {
            logger.error('participant video element not found', { peer_id: participant.participantId });
            return;
          } else if (pDiv.tagName !== 'VIDEO') {
            logger.error('participant element is not a video element', { peer_id: participant.participantId, tag: pDiv.tagName });
            return;
          }

          if (pDiv && pDiv.srcObject !== participant.mediaStream) {
            pDiv.srcObject = participant.mediaStream;
            logger.info('participant stream attached', { peer_id: participant.participantId, stream_id: participant.mediaStream.id });
          }
        });
      }
    },
    onPatientMediaReady() {
      if (this.isPatientCameraPlaying) {
        return;
      }
      this.connecting = false;
      this.setPatientCameraIsPlaying(true);
      // Set local stream after camera is playing and element exists
      this.$nextTick(() => {
        this.setLocalMediaStream();
      });
      this.loadOverlay() // DEMO: remove when done
      if (this.meetingMetaData.test_mode) {
        this.testingTimer = setTimeout(() => {
          this.stopVideo()
        }, this.testVideoDuration)
      }
    },
    disconnectParticipant(participant) {
      // NOTE: this may be called multiple times for the same participant since it's called for each track
      logger.info('participant tracks disconnected', { peer_id: participant.participantId, is_patient: participant.isPatient });
      if (!participant.isPatient) {
        if (this.otherScreenSharePeerId === participant.participantId) {
          // the sharing peer dropped without a clean stop; clear the stale screen-share tile + audio
          this.clearOtherScreenShare();
        }
        if (this.swappedVideoId === `other-video-${participant.participantId}`) {
          this.restorePatientToMain();
        }
        const pIndex = this.otherParticipants.findIndex((p) => p.participantId === participant.participantId);
        if (pIndex !== -1) {
          // remove from the array - vue will remove the control automatically. the tracks will be handled by the unsubscribe handler
          this.otherParticipants.splice(pIndex, 1);
          delete this.participantsVideoActive[participant.participantId];
          const pVid = document.getElementById(`other-video-${participant.participantId}`);
          if (pVid) {
            pVid.srcObject = null;
            logger.debug('participant video element cleared', { peer_id: participant.participantId });
          }
        } else {
          logger.debug('participant already removed', { peer_id: participant.participantId });
        }
      } else {
        // lost the patient connection. disconnect from the room without closing it on the server
        // we leave open on the server so that there's an indication that there's a problem;
        // otherwise it would appear as if the user closed the room
        logger.error('patient connection lost', { meeting_id: this.meetingId });
        this.stopVideo();
        this.showAlert({
          title: 'Error',
          message: 'Video connection lost. Please try to reconnect.'
        });
      }
    },
    async convertPreviewToNormal() {
      if (this.basicMode) {
        logger.warn('cannot convert preview to normal in basic mode', { meeting_id: this.meetingId });
        return false;
      }

      // - verify data channel is open. figure out what to do if not
      // - use data channel to send door knock (if set)
      // - use data channel to activate kiosk (if set)
      // - unmute local audio / show toggle mute button
      if (!this.webrtcSession || !this.webrtcSession.isDeviceDataChannelOpen()) {
        logger.warn('cannot convert preview to normal, no session or data channel closed', { meeting_id: this.meetingId });
        return false;
      }
      this.$emit('update:videoSettings', { ...this.videoSettings, isPreview: false });
      // NOTE: audio only user won't activate kiosk mode but should still be connected to all participants
      if (this.meetingMetaData?.kiosk_mode) {
        this.webrtcSession.enterMeetingRoom(this.$pusher)

        // activate kiosk mode on patient TV and connect user to kiosk screen
        this.webrtcSession.sendDeviceDataChannelMessage({
          type: 'meeting',
          action: 'activate-kiosk',
        })
      }
      if (this.videoSettings.doorKnock) {
        this.webrtcSession.sendDeviceDataChannelMessage({
          type: 'meeting',
          action: 'play-greeting',
        })
      }

      // unmute local audio
      this.webrtcSession.unmuteLocalAudio();
      this.muted = false;

      // call server to mark the meeting as normal
      to(this.$http.put(`/meetings/${this.meetingId}/convert_preview_to_normal`, {}, { skipDivisions: true }))

      return true // if successful
    },
    async connectParticipantScreenShareMedia(participant, stream) {
      // NB: fires once per screen-share track (video + optional audio), all carrying the same stream.
      logger.info('participant screen share connecting', { peer_id: participant.participantId, stream_id: stream.id });

      // A different stream id means a new/replacement share (e.g. the previous sharer dropped without
      // a clean stop). Reset any stale screen-share state first so it can't block this one.
      if (this.otherScreenShareStreamId && this.otherScreenShareStreamId !== stream.id) {
        this.clearOtherScreenShare();
      }
      this.otherScreenShareStreamId = stream.id;
      this.otherScreenSharePeerId = participant.participantId;
      this.otherScreenShareActive = true;
      this.otherScreenShareLabel = `${participant.displayName || participant.participantId}'s Screen`;

      // Route screen-share audio to a dedicated hidden, unmuted sink so it stays audible
      // regardless of which video tile currently shows the screen-share picture.
      this.$nextTick(() => {
        const audioSink = document.getElementById('webrtc-view__screenshare-audio');
        const audioTracks = stream.getAudioTracks();
        if (audioSink && audioTracks.length && audioSink.srcObject == null) {
          audioSink.srcObject = new MediaStream(audioTracks);
        }
      });

      // Attach the video tile + auto-swap once, when a video track is available. The tile gets a
      // video-only stream so audio plays solely through the sink above (no double playback).
      const videoTrack = stream.getVideoTracks()[0];
      if (!videoTrack || this.otherScreenShareVideoAttached) {
        return;
      }
      this.otherScreenShareVideoAttached = true;
      // Only the video track ending tears down the share (an audio track may end on its own).
      videoTrack.addEventListener('ended', () => {
        logger.info('participant screen share ended', { peer_id: participant.participantId });
        this.clearOtherScreenShare();
      });
      this.$nextTick(() => {
        const screenShareElem = document.getElementById('webrtc-view__other-screenshare-view');
        if (!screenShareElem) {
          logger.error('screen share video element not found', { peer_id: participant.participantId });
          return
        }
        const videoOnly = new MediaStream([videoTrack])
        const mainVideo = this.$refs.webrtcView
        if (mainVideo?.srcObject) {
          // auto-swap screen share into main view. thumbnail shows the patient stream
          if (this.originalPatientStream) this.restorePatientToMain()
          this.originalPatientStream = mainVideo.srcObject
          this.swappedVideoId = screenShareElem.id
          mainVideo.srcObject = videoOnly
          screenShareElem.srcObject = this.originalPatientStream
        } else {
          screenShareElem.srcObject = videoOnly
        }
        logger.debug('participant screen share stream set', { peer_id: participant.participantId });
      })
    },
    clearOtherScreenShare() {
      this.otherScreenShareActive = false;
      this.otherScreenShareLabel = '';
      this.otherScreenShareVideoAttached = false;
      this.otherScreenShareStreamId = null;
      this.otherScreenSharePeerId = null;
      this.restorePatientToMain();
      const screenShareElem = document.getElementById('webrtc-view__other-screenshare-view');
      if (screenShareElem) screenShareElem.srcObject = null;
      const audioSink = document.getElementById('webrtc-view__screenshare-audio');
      if (audioSink) audioSink.srcObject = null;
    },
    async startVideo() {
      const token = ++this._startToken // capture token; a stopVideo() call will increment _startToken and invalidate this
      const startedAt = new Date().toISOString() // for the setup record, if the attempt ends before the peer connection
      const webrtcCallId = uuidv4()
      this.log(LOG_TYPES.LIVE_VIDEO.CONNECT_STARTED)
      logger.info('starting video', {
        device_id: this.meetingMetaData.device?.id,
        patient_id: this.meetingMetaData.patient?.id,
        is_preview: this.videoSettings.isPreview,
        two_way_video: this.videoSettings.activateVideo
      })

      let videoReasonIds = []
      if (this.videoSettings.selectedVideoReasons && this.videoSettings.selectedVideoReasons.length > 0) {
        videoReasonIds = this.videoSettings.selectedVideoReasons.map(r => r.id)
      } else if (this.videoSettings.selectedVideoReason) {
        videoReasonIds = [this.videoSettings.selectedVideoReason]
      }

      let postData = {
        device_id: this.meetingMetaData.device.id,
        is_preview: this.videoSettings.isPreview,
        is_video_h265_capable: isVideoH265Capable(),
        patient_id: this.meetingMetaData.patient?.id,
        play_greeting: this.videoSettings.doorKnock,
        ptz: this.meetingMetaData.is_ptz,
        two_way_video: this.videoSettings.activateVideo,
        // screen_share_activated: this.videoSettings.activateScreenShare,
        video_reason_ids: videoReasonIds,
        virtual_request_id: this.meetingMetaData.virtual_request_id,
        webrtc_call_id: webrtcCallId
      }
      this.connecting = true
      this.connectionTimeout = setTimeout(() => {
        if (!this.isPatientCameraPlaying) {
          this.stopVideo()
          this.showAlert("Can't connect to patient's camera")
          this.log(LOG_TYPES.LIVE_VIDEO.VIDEO_CONNECT_FAILED)
        }
      }, 60000)

      let joinUrl = '/videos/join_meeting'

      if (this.meetingMetaData.test_mode) {
        postData = {
          device_id: this.meetingMetaData.device.id,
          is_video_h265_capable: isVideoH265Capable(),
          ptz: this.meetingMetaData.is_ptz,
          webrtc_call_id: webrtcCallId
        }
        joinUrl = '/videos/join_testing'
      }

      const [err, response] = await to(this.http.post(joinUrl, postData, {
        handleErrors: true,
        skipDivisions: true,
        skipLoading: this.suppressDialogs
      }))

      if (!err) {
        const { data: joinData } = response
        this.meetingId = joinData.meeting_id
        this.$emit('meeting-id', this.meetingId)

        const webrtcSession = new WebRTCSession();
        this.webrtcSession = markRaw(webrtcSession);

        // Checkpoint 1: meetingId ✓, webrtcSession ✓ — stopVideo() can now fully clean up
        if (this._startToken !== token) {
          this.reportIncompleteSetupRecord('join', { joinData, stopped: true, startedAt, webrtcCallId })
          logger.info('start video aborted before connection', { meeting_id: this.meetingId });
          this.stopVideo();
          return
        }

        webrtcSession.on(SessionEvents.signalRequired, this.sendSignal);

        webrtcSession.on(SessionEvents.participantTrackAdded, this.connectParticipantMedia);

        webrtcSession.on(SessionEvents.participantScreenShareTrackAdded, this.connectParticipantScreenShareMedia);

        webrtcSession.on(SessionEvents.screenShareStopped, () => {
          this.clearOtherScreenShare();
        });

        // listen for disconnections in order to cleanup
        webrtcSession.on(SessionEvents.participantDisconnected, this.disconnectParticipant);

        // listen for disconnected from room event
        webrtcSession.on(SessionEvents.disconnected, (error) => {
          // error is null/undefined when disconnected by us so ignore that case
          if (error != null) {
            logger.error('video room disconnected unexpectedly', { meeting_id: this.meetingId, reason: error });

            let err_message = "You were disconnected from the video room. Please try again.";

            // clean up
            this.stopVideo(); // no wait needed

            this.showAlert({
              title: 'Error',
              message: err_message,
            });

            this.log(LOG_TYPES.LIVE_VIDEO.VIDEO_DISCONNECT_UNEXPECTED)
          } else {
            logger.debug('ignoring expected disconnect event', { meeting_id: this.meetingId });
            this.log(LOG_TYPES.LIVE_VIDEO.VIDEO_DISCONNECT_EXPECTED)
          }
        });

        webrtcSession.on(SessionEvents.webrtcNetworkScoreUpdated, (metrics) => {
          this.saveVideoMetrics('stats', metrics);
        });
        webrtcSession.on(SessionEvents.webrtcIssue, (issue) => {
          this.saveVideoMetrics('warning', issue);
        });
        webrtcSession.on(SessionEvents.connectSucceeded, this.onConnectSucceeded)
        webrtcSession.on(SessionEvents.patientMediaReady, this.onPatientMediaReady)

        webrtcSession.on(SessionEvents.webrtcQualityUpdated, ({ scores, issues }) => {
          if (!this.isQualityIndicatorEnabled || !this.$refs.qualityIndicator) {
            return;
          }
          if (scores?.inbound) {
            this.$refs.qualityIndicator.score = scores.inbound
            this.$refs.qualityIndicator.stats = scores.statsSamples.inboundStatsSample
          }
          if (issues) {
            logger.warn('webrtc quality issues detected', { meeting_id: this.meetingId, issues: issues.map(i => `${i.type}/${i.reason}`) });
            this.$refs.qualityIndicator.issues = issues
          }
        });

        const statsInterval = 10000 // Always collect stats every 10s for video meetings.
        const roomType = this.meetingMetaData.test_mode ? SessionTypes.test : SessionTypes.meeting
        let stream;

        const constraints = {
          audio: true,
          // limit video being sent b/c it is either displayed small or in kiosk mode where it's limited by CPU power on device
          video: this.videoSettings.activateVideo ? { width: 480, aspectRatio: 1.7777777778, frameRate: { min: 15 } } : false,
        }
        stream = await this.requestUserMedia(constraints, 'video meeting',
          (err) => this.reportIncompleteSetupRecord('user_media', { joinData, err, stopped: this._startToken !== token, startedAt, webrtcCallId }))

        // Checkpoint 2: meetingId ✓, webrtcSession ✓ — the new stream is not this.localMediaStream yet,
        // so stopVideo() won't stop its tracks. A failed getUserMedia already sent its setup record.
        if (this._startToken !== token) {
          if (stream) {
            this.reportIncompleteSetupRecord('user_media', { joinData, stopped: true, startedAt, webrtcCallId })
            stream.getTracks().forEach((track) => track.stop())
          }
          logger.info('start video aborted before acquiring media', { meeting_id: this.meetingId });
          this.stopVideo();
          return
        }

        if (!stream) {
          this.connecting = false
          clearTimeout(this.connectionTimeout)
          this.$emit('close')
          return
        }
        // isPreview and startMuted both join with the local mic disabled at capture time. startMuted
        // is used by the hidden kiosk meeting so the observer's audio travels only over the monitoring
        // connection (one live mic, no echo) while their video still streams to the kiosk.
        if (this.videoSettings.isPreview || this.videoSettings.startMuted) {
          stream.getAudioTracks().forEach((track) => {
            track.enabled = false
          });
          this.muted = true
        }
        this.localMediaStream = markRaw(stream)

        // Local stream will be set when patient camera starts playing
        await webrtcSession.connect(joinData, roomType, this.localMediaStream, {
          statsIntervalMs: statsInterval,
          userDisplayName: this.videoSettings.visitorName,
          webrtcQualityEnabled: this.isQualityIndicatorEnabled,
          telemetry: buildViewerTelemetryOptions(this.$store, joinData, { camera: this.meetingMetaData.is_ptz ? 'ptz' : 'internal', webrtcCallId }),
        })

        // Checkpoint 3: meetingId ✓, webrtcSession connected ✓, localMediaStream ✓ — stopVideo() tears down the live connection
        if (this._startToken !== token) {
          logger.info('start video aborted before entering meeting room', { meeting_id: this.meetingId });
          this.stopVideo();
          return
        }

        this.basicMode = joinData.basic_mode

        if (!joinData.basic_mode && !this.videoSettings.isPreview) {
          webrtcSession.enterMeetingRoom(this.$pusher)
        }

      } else {
        this.reportIncompleteSetupRecord('join', { err, stopped: this._startToken !== token, startedAt, webrtcCallId })
        logger.error('failed to open video room', { device_id: this.meetingMetaData.device?.id }, err);
        let msg = 'Video session could not connect or was disconnected. Please check that monitoring has not been stopped and your network connection is functioning'
        if (err.response?.data?.error == 'meeting-another-camera-in-use') {
          const ptzFailed = !this.meetingMetaData.is_ptz
          msg = `A meeting is in progress with the ${ptzFailed ? 'Pan-Tilt-Zoom' : 'core'} camera. Please reconnect with Pan-Tilt-Zoom Camera option ${ptzFailed ? 'checked' : 'unchecked'} to join the meeting.`
        }
        this.showAlert({
          title: 'Error',
          message: msg
        });
        this.log(LOG_TYPES.LIVE_VIDEO.VIDEO_CONNECT_FAILED)
        this.connecting = false
        clearTimeout(this.connectionTimeout)
        this.$emit('close')
      }
    },
    async saveVideoMetrics(metricType, payload) {
      const deviceId = this.meetingMetaData.device?.id,
        divisionId = this.meetingMetaData.patient_monitor?.division_id || this.meetingMetaData.patient?.division_id
      if (!deviceId || !divisionId) {
        return;
      }

      const postData = payload.map(pl => {
        return {
          timestamp: pl.timestamp,
          metric_type: metricType,
          video_device_id: deviceId,
          division_id: divisionId,
          meeting_id: this.meetingId,
          metrics: pl.metrics
        }
      })
      to(this.http.post('/videos/save_metrics', { video_device_metrics: postData }, {
        handleErrors: false,
        skipLoading: true,
        skipDivisions: true,
        skipInactivityCheck: true
      }))
    },
    async transcribeToPatientTV() {
      if (!this.webrtcSession) {
        logger.warn('no active session for transcription to patient tv');
        return;
      }

      const patientMonitorId = await this.getPatientMonitorId();

      logger.info('starting caregiver transcription to patient tv', { patient_monitor_id: patientMonitorId });

      const thisView = this;
      function onTranscriptionLine(transcriptionLine) {
        logger.debug('transcription line sent to patient tv', { line_chars: transcriptionLine?.length });
        // kiosk is the TV display
        thisView.webrtcSession.sendKioskDataChannelMessage({
          type: 'transcription-line',
          line: transcriptionLine,
          speaker: thisView.videoSettings?.visitorName
        })
      }

      const micStream = await this.requestUserMedia({ audio: true, video: false }, 'transcription')
      if (!micStream) return
      this.transcriptionLocalMicStream = markRaw(micStream)
      this.transcriptionSessionToPatientTV = markRaw(new TranscriptionSession())
      this.transcriptionSessionToPatientTV.start(patientMonitorId, this.transcriptionLocalMicStream, 'caregiver', onTranscriptionLine, this.$http)
    },
    stopTranscriptionToPatientTV() {
      if (this.transcriptionSessionToPatientTV) {
        this.transcriptionSessionToPatientTV.stop();
        this.transcriptionSessionToPatientTV = null;
        if (this.transcriptionLocalMicStream) {
          this.transcriptionLocalMicStream.getTracks().forEach((track) => {
            track.stop();
          });
          this.transcriptionLocalMicStream = null;
          logger.info('stopped transcription to patient tv');
        }
      }
    },
    stopVideo() {
      this._startToken++ // invalidate any in-flight startVideo() call
      const wasActive = this.connecting || this.isPatientCameraPlaying // capture before reset
      logger.info('stopping video', { meeting_id: this.meetingId, was_active: wasActive });
      this.onConnectSucceeded?.reset();
      this.transcriptionEnabled = false;
      // this.$emit('transcription-toggled', false);
      this.cachedPatientMonitorId = null;
      this.stopTranscriptionToPatientTV();
      clearTimeout(this.connectionTimeout);
      clearTimeout(this.testingTimer)
      // clean up
      if (this.webrtcSession) {
        logger.debug('disconnecting active session', { meeting_id: this.meetingId });
        // disconnect streams from video html elements so they can be closed properly
        const localElem = document.getElementById("webrtc-view__local-view");
        if (localElem) {
          localElem.srcObject = null;
        }
        const patientElem = document.getElementById('webrtc-view__patient-view');
        if (patientElem) {
          patientElem.srcObject = null;
        }

        this.otherParticipants.forEach((p) => {
          const pVid = document.getElementById(`other-video-${p.participantId}`);
          if (pVid) {
            pVid.srcObject = null;
            logger.debug('participant video element cleared', { peer_id: p.participantId });
          }
        });

        // make sure we disconnect from the room after closing tracks and cleaning up elements
        this.webrtcSession.leaveMeetingRoom(this.$pusher);
        this.webrtcSession.disconnect();
        logger.info('disconnected from video', { meeting_id: this.meetingId })
        this.webrtcSession = null;
      }
      this.otherParticipants.splice(0, this.otherParticipants.length);
      if (this.meetingId) {
        to(this.$http.put(`/meetings/${this.meetingId}/leave`, null, { skipDivisions: true, skipLoading: this.suppressDialogs }))
      }
      if (this.localMediaStream) {
        this.localMediaStream.getTracks().forEach((track) => {
          track.stop();
        });
        this.localMediaStream = null;
      }
      if (this.localScreenShareMediaStream) {
        this.localScreenShareMediaStream.getTracks().forEach((track) => {
          track.stop();
        });
        this.localScreenShareMediaStream = null;
      }
      this.connecting = false;
      this.setPatientCameraIsPlaying(false)
      this.muted = false;
      this.overlayConfig = null; // DEMO: remove when done
      this.localScreenShareActive = false;
      this.otherScreenShareActive = false;
      this.otherScreenShareLabel = '';
      this.otherScreenShareVideoAttached = false;
      this.otherScreenShareStreamId = null;
      this.otherScreenSharePeerId = null;
      const screenShareAudioElem = document.getElementById('webrtc-view__screenshare-audio');
      if (screenShareAudioElem) screenShareAudioElem.srcObject = null;
      this.originalPatientStream = null;
      this.swappedVideoId = null;
      this.$refs.qualityIndicator?.reset()
      if (wasActive) this.$emit('close') // only emit if we were actually active (prevents double-emit from abort checkpoints)
    },
    async sendSignal(signalPayload) {
      signalPayload.patient_monitor_id = this.meetingMetaData.patient_monitor?.id;
      signalPayload.video_device_id = this.meetingMetaData.device.id;
      signalPayload.meeting_id = this.meetingId
      signalPayload.ptz = this.meetingMetaData.is_ptz
      logger.debug('sending signal', { signal_type: signalPayload.signal_type, meeting_id: this.meetingId });
      const [err] = await to(this.http.post('/videos/signal', signalPayload, { skipDivisions: true, skipLoading: true }));
      if (err) {
        logger.error('failed to send signal', { signal_type: signalPayload.signal_type, meeting_id: this.meetingId }, err);
        this.showAlert({
          title: 'Error',
          message: 'Video session could not connect or was disconnected. Please check that monitoring has not been stopped and your network connection is functioning'
        });
        this.log(LOG_TYPES.LIVE_VIDEO.VIDEO_CONNECT_FAILED)
        this.stopVideo()
      }
    },
    toggleMute() {
      if (this.muted) {
        this.webrtcSession.unmuteLocalAudio();
        this.muted = false;
      } else {
        this.webrtcSession.muteLocalAudio();
        this.muted = true;
      }
    },
    setLocalMediaStream() {
      if (this.localMediaStream) {
        const localElem = document.getElementById("webrtc-view__local-view");
        if (localElem) {
          localElem.srcObject = this.localMediaStream;
          logger.debug('local media stream attached', { stream_id: this.localMediaStream.id });
        }
      }
    },
    setLocalScreenShareMediaStream() {
      if (this.localScreenShareMediaStream) {
        const screenShareElem = document.getElementById("webrtc-view__local-screenshare-view");
        if (screenShareElem) {
          screenShareElem.srcObject = this.localScreenShareMediaStream;
          logger.debug('local screen share stream attached', { stream_id: this.localScreenShareMediaStream.id });
        }
      }
    },
    ...mapMutations('LiveVideoStore', ['setPatientCameraIsPlaying']),
    log(type) {
      if (!this.meetingMetaData.test_mode) {
        const videoType = this.meetingMetaData.activateVideo ? 'monitoring' : 'meeting'
        let videoReasons = null
        let selectedVideoReasonIds = []

        if (this.videoSettings?.selectedVideoReason) {
          selectedVideoReasonIds = [this.videoSettings.selectedVideoReason]
        } else {
          selectedVideoReasonIds = this.videoSettings?.selectedVideoReasons?.map(x => x.id) || []
        }
        videoReasons = this.videoSettings?.availableVideoReasons?.filter(x => selectedVideoReasonIds.includes(x.id))?.map(x => x.name)?.join(', ')

        this.$logger.logLiveVideo(
          type,
          videoType,
          this.meetingMetaData?.patient?.id,
          this.meetingMetaData?.patient_monitor?.id,
          this.meetingId,
          this.meetingMetaData?.is_ptz,
          videoReasons
        )
      }
    },
    toggleScreenShare() {
      if (this.localScreenShareActive) {
        this.stopScreenShare();
      } else {
        this.startScreenShare();
      }
    },
    async startScreenShare() {
      logger.info('starting local screen share', { meeting_id: this.meetingId });
      // Request screen video plus optional screen/tab audio. An audio track is only granted by
      // some browsers/sources (Chrome/Edge tab audio, Windows/ChromeOS entire-screen) and is
      // frequently absent (macOS, window shares, Safari, Firefox), so it is treated as optional
      // everywhere downstream. The user's mic stays in localMediaStream - it is never mixed in here.
      this.localScreenShareMediaStream = await this.requestDisplayMedia({ video: true, audio: true })
      if (!this.localScreenShareMediaStream) {
        logger.info('screen share not started, no media stream obtained', { meeting_id: this.meetingId });
        return;
      }

      // handle browser-initiated screen share stop (e.g. user clicks "Stop sharing")
      const videoTrack = this.localScreenShareMediaStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.addEventListener('ended', () => {
          this.localScreenShareActive = false;
          // if the share was swapped into the main view, put the patient stream back first -
          // otherwise main keeps pointing at the stream we're about to stop (black screen)
          if (this.swappedVideoId === 'webrtc-view__local-screenshare-view') {
            this.restorePatientToMain();
          }
          // stop every remaining track (notably the screen-share audio track) before dropping
          // the reference, otherwise the audio keeps capturing after sharing has stopped
          const stream = this.localScreenShareMediaStream;
          stream?.getTracks().forEach((track) => track.stop());
          this.localScreenShareMediaStream = null;
          const screenShareElem = document.getElementById("webrtc-view__local-screenshare-view");
          if (screenShareElem) screenShareElem.srcObject = null;
        });
      }

      this.localScreenShareActive = true;
      this.webrtcSession.activateScreenShare(this.localScreenShareMediaStream);
      this.$nextTick(() => {
        // make sure the video element is in the DOM before setting the stream
        this.setLocalScreenShareMediaStream();
      });
    },
    stopScreenShare() {
      logger.info('stopping local screen share', { meeting_id: this.meetingId });
      this.localScreenShareActive = false;
      // if the share was swapped into the main view, put the patient stream back first -
      // otherwise main keeps pointing at the stream we're about to stop (black screen)
      if (this.swappedVideoId === 'webrtc-view__local-screenshare-view') {
        this.restorePatientToMain();
      }
      this.webrtcSession.deactivateScreenShare(this.localScreenShareMediaStream);
      if (this.localScreenShareMediaStream) {
        this.localScreenShareMediaStream.getTracks().forEach((track) => {
          track.stop();
        });
        this.localScreenShareMediaStream = null;
      }
      const screenShareElem = document.getElementById("webrtc-view__local-screenshare-view");
      if (screenShareElem) {
        screenShareElem.srcObject = null;
      }
    },
    toggleTranscription() {
      this.transcriptionEnabled = !this.transcriptionEnabled;
      logger.info('transcription toggled', { enabled: this.transcriptionEnabled, meeting_id: this.meetingId });
      // this.$emit('transcription-toggled', this.transcriptionEnabled);

      if (this.transcriptionEnabled) {
        // If video is active and kiosk mode is enabled, also transcribe to patient TV
        if (this.videoSettings?.activateVideo && this.meetingMetaData?.kiosk_mode) {
          this.transcribeToPatientTV();
        }
      } else {
        // Stop transcription to patient TV when CC is disabled
        this.stopTranscriptionToPatientTV();
      }
    },
    async getPatientMonitorId() {
      // Return cached value if available
      if (this.cachedPatientMonitorId) {
        return this.cachedPatientMonitorId;
      }

      let monitorId = this.meetingMetaData?.patient_monitor?.id
      if (!monitorId) {
        // depending on the entry point, the monitor may not be set
        monitorId = this.meetingMetaData?.patient?.patient_monitors?.find(x => x.status === 'active')?.id
        if (!monitorId) {
          const [err, response] = await to(this.$http.get(`/patients/${this.meetingMetaData?.patient?.id}/monitoring_sessions`))
          if (!err) {
            const monitor = response.data.find(x => x.status === 'active')
            if (monitor) {
              monitorId = monitor.id
            } else {
              logger.warn('no active monitor found for patient', { patient_id: this.meetingMetaData?.patient?.id })
            }
          } else {
            logger.error('failed to get patient monitors', { patient_id: this.meetingMetaData?.patient?.id }, err)
          }
        }
      }

      // Cache the result before returning
      this.cachedPatientMonitorId = monitorId;
      return monitorId;
    },
    getPatientAudioStream() {
      // Get only the audio tracks from the patient's stream (video not needed for transcription)
      if (this.$refs.webrtcView && this.$refs.webrtcView.srcObject) {
        const audioTracks = this.$refs.webrtcView.srcObject.getAudioTracks();
        if (audioTracks.length > 0) {
          return new MediaStream(audioTracks);
        }
      }
      return null;
    },
    // Central alert gate: hidden/embedded instances (suppressDialogs) log instead of popping a modal.
    showAlert(options) {
      if (this.suppressDialogs) {
        const o = typeof options === 'string' ? { message: options } : (options || {})
        logger.warn('dialog suppressed', { title: o.title, message: o.message })
        return
      }
      this.$buefy.dialog.alert(options)
    },
    async requestUserMedia(constraints, context = '', onError = null) {
      const [err, stream] = await to(navigator.mediaDevices.getUserMedia(constraints))
      if (err) {
        onError?.(err)
        logger.error('failed to get user media', { context, error_name: err?.name }, err)
        this.showAlert({
          title: 'Media Error',
          message: describeWebrtcError(err),
          type: 'is-danger'
        })
        return null
      }
      return stream
    },
    // A web telemetry setup record for an attempt that ended before its peer connection existed
    // (design/webrtc-viewer-telemetry.md, "Setup records"). Covers meetings and Test Live Video.
    reportIncompleteSetupRecord(stage, { joinData = null, err = null, stopped = false, startedAt = null, webrtcCallId = null } = {}) {
      const meta = this.meetingMetaData || {}
      sendIncompleteSetupRecord(this.$store, {
        joinData,
        deviceId: meta.device?.id,
        channel: meta.test_mode ? SessionTypes.test : SessionTypes.meeting,
        isPtz: meta.is_ptz === true,
        startedAt,
        webrtcCallId
      }, { stage, err, stopped })
    },
    async requestDisplayMedia(constraints) {
      const [err, stream] = await to(navigator.mediaDevices.getDisplayMedia(constraints))
      if (err) {
        if (err.name === "NotAllowedError") {
          logger.info('screen share cancelled by user')
        } else {
          logger.error('failed to get display media', { error_name: err?.name }, err)
          this.showAlert({
            title: 'Media Error',
            message: 'Unable to share screen.',
            type: 'is-danger'
          })
        }
        return null
      }
      return stream
    },
    onLocalBlindsToggle({ isClosed }) {
      this.localBlindsOverride = !isClosed
    },
    swapWithMain(event) {
      const clickedVideo = event.currentTarget.querySelector('video')
      if (!clickedVideo?.srcObject) {
        return
      }
      const mainVideo = this.$refs.webrtcView
      if (!mainVideo?.srcObject) {
        return
      }

      // clicking the thumbnail that currently has patient stream = swap back
      if (this.swappedVideoId && clickedVideo.id === this.swappedVideoId) {
        this.restorePatientToMain()
        return
      }

      // if already swapped with something else, restore first
      if (this.originalPatientStream) {
        this.restorePatientToMain()
      }

      this.originalPatientStream = mainVideo.srcObject
      this.swappedVideoId = clickedVideo.id

      mainVideo.srcObject = clickedVideo.srcObject
      clickedVideo.srcObject = this.originalPatientStream
    },
    labelFor(id, fallback) {
      return this.swappedVideoId === id ? 'Patient Camera' : fallback
    },
    restorePatientToMain() {
      if (!this.originalPatientStream) {
        return
      }
      const mainVideo = this.$refs.webrtcView
      if (mainVideo) {
        if (this.swappedVideoId) {
          const swappedEl = document.getElementById(this.swappedVideoId)
          if (swappedEl) {
            swappedEl.srcObject = mainVideo.srcObject
          }
        }
        mainVideo.srcObject = this.originalPatientStream
      }
      this.originalPatientStream = null
      this.swappedVideoId = null
    },
  },
  computed: {
    ...mapState('LiveVideoStore', ['isPatientCameraPlaying', 'requestVideoStart']),
    // DEMO: remove this computed when done
    overlayIframeSrc() {
      const monitorId = this.cachedPatientMonitorId || this.meetingMetaData?.patient_monitor?.id
      return buildOverlayIframeSrc(this.overlayConfig, this.meetingMetaData?.patient?.id, monitorId)
    },
    connectButtonText() {
      return this.cancelable ? "Cancel" : "Video Visit"
    },
    cancelable() {
      return this.connecting && this.webrtcSession
    },
    isWifiNetworkInterface() {
      return this.meetingMetaData?.device?.network_interface?.toLowerCase() == 'wifi'
    },
    // leaving this in case if we ever want to disable indicator
    isQualityIndicatorEnabled() {
      return true
    },
    isClosedCaptionsEnabled() {
      return this.$store.state.currentUser.enabled_flags.includes('closed_captions')
    },
    isGuestUser() {
      return this.$store.state.currentUser?.role?.kind === 'guest'
    },
    isScreenShareEnabled() {
      return this.basicMode === false
        && this.$store.state.currentUser?.role?.kind !== 'guest'
        && this.$store.state.currentUser.enabled_flags.includes('screen_sharing')
        && this.meetingMetaData?.kiosk_mode === true
    },
    supportsNightMode() {
      return this.meetingMetaData?.device?.version &&
        semver.gte(this.meetingMetaData.device.version, '1.17.16') &&
        (
          (this.meetingMetaData.is_ptz && this.meetingMetaData?.device?.config?.webrtc?.secondary_camera?.video_source_connection?.profile_name === 'vhd-ptz-usb') ||
          (!this.meetingMetaData.is_ptz && this.meetingMetaData?.device?.config?.webrtc?.primary_camera?.video_source_connection?.profile_name === 'vhd-videobar-usb-1')
        )
    },
    wifiMetrics() {
      return {
        state: this.meetingMetaData.device?.state?.metrics?.wifi_signal_state,
        strength: this.meetingMetaData.device?.state?.metrics?.wifi_signal_strength
      }
    },
    effectiveBlinds() {
      return this.blindsClosed && !this.localBlindsOverride
    }
  },
  mounted() {
    if (this.startAutomatically) {
      this.startVideo()
    }
    this.onConnectSucceeded = this.$helpers.once(() => {
      this.log(LOG_TYPES.LIVE_VIDEO.CONNECT_SUCCEEDED)
    })
  },
  created() {
    this._startToken = 0; // non-reactive cancellation token for in-flight startVideo() calls
    // add a window listener to make sure we disconnect from the room if the user navigates away or closes the window
    window.addEventListener('beforeunload', this.stopVideo);
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.stopVideo)
    // ensure screen-share capture (incl. its audio track) and the session are torn down if the
    // component is unmounted (e.g. route change) while a call/share is still active
    this.stopVideo()
  },
  watch: {
    requestVideoStart: {
      handler(newValue) {
        if (newValue && !this.startAutomatically) {
          this.startVideo()
        }
      },
      immediate: true
    },
    blindsClosed(newVal) {
      if (newVal) {
        this.localBlindsOverride = false
      }
    }
  },
  data() {
    return {
      connectionTimeout: null,
      testingTimer: null,
      testVideoDuration: 5 * 60 * 1000,
      connecting: false,
      basicMode: true,
      /**
       * @type {?WebRTCSession}
       */
      webrtcSession: null,
      otherParticipants: [],
      participantsVideoActive: {},
      muted: false,
      meetingId: null,
      onConnectSucceeded: null,
      transcriptionEnabled: false,
      cachedPatientMonitorId: null,
      transcriptionSessionToPatientTV: null,
      transcriptionLocalMicStream: null,
      ccOnIcon,
      ccSlashIcon,
      overlayConfig: null, // DEMO: remove when done
      localMediaStream: null,
      localScreenShareMediaStream: null,
      localScreenShareActive: false,
      otherScreenShareActive: false,
      otherScreenShareLabel: '',
      otherScreenShareVideoAttached: false,
      otherScreenShareStreamId: null,
      otherScreenSharePeerId: null,
      originalPatientStream: null,
      swappedVideoId: null,
      localBlindsOverride: false,
    }
  }
}
</script>

<style scoped lang="scss">
.webrtc-view__tracks {
  position: relative;
  width: 100%;
  height: 100%;
}

.webrtc-view__settings {
  position: absolute;
  top: 10px;
  left: 12px;
  z-index: 2;
}

.webrtc-view__not-connected {
  position: relative;
  top: 0;
  width: 100%;
  height: 100%;
  background-image: url("@/assets/blurry_hospital.jpg");
  background-size: cover;
  background-position: center center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  button {
    margin-top: 10px;
  }
}

#webrtc-view__other-participants {
  top: 10px;
  right: 10px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: hidden;
  z-index: 19;
  margin: 20px 10px;
  padding: 5px;
}

:deep() {
  #webrtc-view__patient-view {
    width: 100%;
    height: 100%;
  }
}

.participant-container {
  position: relative;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  flex: 1 1 0;
  min-height: 0;
  aspect-ratio: 16 / 9;
  width: auto;
  max-width: 260px;
  align-self: flex-end;
  max-height: 148px;
  box-shadow: 0px 0px 3px 1px #0000008a;
}

.participant-container video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.participant-name {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.participant-avatar {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: bold;
  color: white;
  user-select: none;
  z-index: 1;
}


.webrtc-view__video-controls {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hangup-button {
  background-color: rgb(247 79 79 / 56%) !important;
  border: solid 1px #ff7d7d !important;

  &:hover {
    background-color: #ff7d7d !important;
  }
}

.webrtc-view__video-control {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  margin: 0 10px;
  background-color: #4fc3f78f;
  color: #fff;
  border: solid 1px #7dd7ff;
  opacity: 0;
  transition: all 0.1s linear;
  box-shadow: none !important;
  z-index: 99999;

  i {
    padding-top: 5px;
    font-size: 23px;
  }

}

.webrtc-view__video-control:hover:not(:disabled) {
  background-color: #4fc3f7;
  color: #fff;
  border: solid 1px #7dd7ff;
}

.webrtc-view__video-control:disabled {
  opacity: 0 !important;
  cursor: not-allowed;
  pointer-events: auto;
  background-color: #4fc3f78f !important;
  color: #fff !important;
  border: solid 1px #7dd7ff !important;
}

.webrtc-view__video-control.transcription-active {
  background-color: #4fc3f7;
  border: solid 1px #7dd7ff;
}

.webrtc-view__connecting-text {
  background: rgba(181, 181, 181, 0.9);
  color: #363636;
  padding: 4px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.webrtc-view__video-container {
  position: relative;
  overflow: hidden;

  width: 100%;
  height: 100%;

  &:hover .webrtc-view__video-control {
    opacity: 1;
  }

  &:hover .webrtc-view__video-control:disabled {
    opacity: 0.35 !important;
  }
}

.video-ptz {
  z-index: 999999;
  margin-left: auto;
}

/* DEMO: remove overlay styles when done */
.webrtc-overlay-bar {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 19;
  overflow: hidden;
}

.webrtc-overlay-iframe {
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
  display: block;
  color-scheme: normal;
}

.open-mic-warning {
  position: absolute;
  width: 100%;
  left: 0;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
  font-size: 11px;
  letter-spacing: 2px;
  font-weight: bold;
  color: #fff;
  padding: 3px;
  background: red;
}

@include until($desktop) {
  .webrtc-view__video-controls {
    position: absolute;
    top: initial;
    bottom: 10px;
  }

  .webrtc-view__video-control {
    opacity: 1 !important;
  }

  .video-ptz {
    display: none;
  }

  .webrtc-view__local-view {
    display: none;
  }

  .webrtc-view__local-screenshare-view {
    display: none;
  }
}

.bottom-row {
  // position: absolute;
  bottom: 5px;
  width: 100%;
  z-index: 9999;
  display: flex;
  padding: 10px 10px;
}

.overlay-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
}
</style>
